// src/pages/SetPassword.jsx
import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { extractError } from '../context/AuthContext';

export default function SetPassword() {
  const [params]  = useSearchParams();
  const navigate  = useNavigate();
  const token     = params.get('token') || '';

  const [form,    setForm]    = useState({ new_password: '', confirm_password: '' });
  const [show,    setShow]    = useState({ new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);

  const pw = form.new_password;
  const strength = (() => {
    let s = 0;
    if (pw.length >= 8)           s++;
    if (/[A-Z]/.test(pw))         s++;
    if (/[0-9]/.test(pw))         s++;
    if (/[^A-Za-z0-9]/.test(pw))  s++;
    return s;
  })();
  const strLabel = ['', 'Faible', 'Moyen', 'Bon', 'Fort'];
  const strColor = ['', '#EF4444', '#F97316', '#D97706', '#22C55E'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas'); return;
    }
    if (form.new_password.length < 8) {
      toast.error('Au moins 8 caractères requis'); return;
    }
    setLoading(true);
    try {
      await api.post('/admin/set-password', {
        token,
        new_password:     form.new_password,
        confirm_password: form.confirm_password,
      });
      setDone(true);
    } catch (err) {
      toast.error(extractError(err));
    } finally { setLoading(false); }
  };

  if (!token) return (
    <Page>
      <div style={{ textAlign:'center', padding:'8px 0' }}>
        <div style={s.iconWrap('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.30)')}>
          <AlertCircle size={30} color="#F87171"/>
        </div>
        <h2 style={s.title}>Lien invalide</h2>
        <p style={s.sub}>Le paramètre token est manquant dans l'URL.<br/>Contactez votre administrateur.</p>
      </div>
    </Page>
  );

  if (done) return (
    <Page>
      <div style={{ textAlign:'center', padding:'8px 0' }}>
        <div style={s.iconWrap('rgba(34,197,94,0.12)', 'rgba(34,197,94,0.30)')}>
          <CheckCircle size={30} color="#22C55E"/>
        </div>
        <h2 style={s.title}>Mot de passe créé !</h2>
        <p style={{ ...s.sub, marginBottom:28 }}>
          Votre compte WIBusiness est prêt.<br/>
          Vous pouvez maintenant vous connecter.
        </p>
        <button onClick={() => navigate('/login')} style={s.btnPrimary}>
          Se connecter à la plateforme
        </button>
      </div>
    </Page>
  );

  return (
    <Page>
      {/* Icône */}
      <div style={{ display:'flex', justifyContent:'center', marginBottom:20 }}>
        <div style={s.iconWrap('rgba(59,130,246,0.12)', 'rgba(59,130,246,0.28)')}>
          <Lock size={24} color="#3B82F6"/>
        </div>
      </div>

      <h2 style={{ ...s.title, textAlign:'center', marginBottom:6 }}>
        Créer votre mot de passe
      </h2>
      <p style={{ ...s.sub, textAlign:'center', marginBottom:28 }}>
        Choisissez un mot de passe sécurisé pour accéder à la plateforme.
      </p>

      <form onSubmit={handleSubmit}>
        {/* Nouveau mot de passe */}
        <div style={{ marginBottom:16 }}>
          <label style={s.label}>Nouveau mot de passe</label>
          <div style={{ position:'relative' }}>
            <input
              style={s.input}
              type={show.new ? 'text' : 'password'}
              value={form.new_password}
              onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))}
              placeholder="Minimum 8 caractères"
              required
            />
            <button type="button" style={s.eye}
              onClick={() => setShow(v => ({ ...v, new: !v.new }))}>
              {show.new ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>

          {pw.length > 0 && (
            <div style={{ marginTop:8 }}>
              <div style={{ display:'flex', gap:4, marginBottom:5 }}>
                {[1,2,3,4].map(i => (
                  <div key={i} style={{
                    flex:1, height:4, borderRadius:2,
                    background: i <= strength ? strColor[strength] : 'rgba(255,255,255,0.08)',
                    transition:'background 0.25s',
                  }}/>
                ))}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:'0.75rem', color: strColor[strength], fontWeight:700 }}>
                  {strLabel[strength]}
                </span>
                <span style={{ fontSize:'0.6875rem', color:'rgba(255,255,255,0.38)' }}>
                  {strength < 3 ? 'Ajoutez majuscules, chiffres et symboles' : 'Excellent !'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Confirmation */}
        <div style={{ marginBottom:28 }}>
          <label style={s.label}>Confirmer le mot de passe</label>
          <div style={{ position:'relative' }}>
            <input
              style={{
                ...s.input,
                borderColor: form.confirm_password && form.new_password !== form.confirm_password
                  ? 'rgba(239,68,68,0.6)'
                  : form.confirm_password && form.new_password === form.confirm_password
                  ? 'rgba(34,197,94,0.6)'
                  : 'rgba(255,255,255,0.10)',
              }}
              type={show.confirm ? 'text' : 'password'}
              value={form.confirm_password}
              onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))}
              placeholder="Répétez votre mot de passe"
              required
            />
            <button type="button" style={s.eye}
              onClick={() => setShow(v => ({ ...v, confirm: !v.confirm }))}>
              {show.confirm ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
          {form.confirm_password && form.new_password !== form.confirm_password && (
            <div style={{ fontSize:'0.75rem', color:'#F87171', marginTop:5, fontWeight:600 }}>
              Les mots de passe ne correspondent pas
            </div>
          )}
          {form.confirm_password && form.new_password === form.confirm_password && (
            <div style={{ fontSize:'0.75rem', color:'#4ADE80', marginTop:5, fontWeight:600 }}>
              ✓ Les mots de passe correspondent
            </div>
          )}
        </div>

        <button type="submit" disabled={loading} style={{
          ...s.btnPrimary,
          opacity: loading ? 0.70 : 1,
          cursor: loading ? 'not-allowed' : 'pointer',
          width: '100%',
        }}>
          {loading
            ? <span style={{ display:'inline-flex', alignItems:'center', gap:8 }}>
                <span style={{ width:16, height:16, border:'2px solid rgba(255,255,255,0.30)',
                  borderTopColor:'white', borderRadius:'50%', display:'inline-block',
                  animation:'sp-spin 0.8s linear infinite' }}/>
                Enregistrement…
              </span>
            : 'Créer mon mot de passe'
          }
        </button>
      </form>

      <div style={{ marginTop:20, padding:'10px 14px', background:'var(--bg-card)',
        borderRadius:8, border:'1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.38)', lineHeight:1.6 }}>
          Ce lien est valable <strong style={{ color:'rgba(255,255,255,0.60)' }}>48 heures</strong> et ne peut être utilisé qu'une seule fois.
          Si votre lien a expiré, contactez votre administrateur pour en obtenir un nouveau.
        </div>
      </div>
    </Page>
  );
}

/* ── Conteneur page ── */
function Page({ children }) {
  return (
    <div style={{
      minHeight:'100vh',
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      background:'#000000',
      padding:24,
      position:'relative',
      overflow:'hidden',
    }}>
      {/* Orbs */}
      <div style={{ position:'absolute', width:500, height:500, top:-150, left:-100,
        borderRadius:'50%', background:'radial-gradient(circle, rgba(59,130,246,0.14) 0%, transparent 65%)',
        filter:'blur(100px)', pointerEvents:'none' }}/>
      <div style={{ position:'absolute', width:350, height:350, bottom:-80, right:-60,
        borderRadius:'50%', background:'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 65%)',
        filter:'blur(100px)', pointerEvents:'none' }}/>

      {/* Grid overlay */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none',
        backgroundImage:'linear-gradient(rgba(255,255,255,0.022) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.022) 1px,transparent 1px)',
        backgroundSize:'60px 60px',
        WebkitMaskImage:'linear-gradient(to bottom,rgba(0,0,0,0.5),rgba(0,0,0,0.05))',
        maskImage:'linear-gradient(to bottom,rgba(0,0,0,0.5),rgba(0,0,0,0.05))',
      }}/>

      {/* Logo — 3 barres setycorp */}
      <div style={{ marginBottom:32, textAlign:'center', position:'relative', zIndex:1 }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:14 }}>
          <svg width="32" height="36" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <g transform="skewX(-18)">
              <rect x="0"  y="0"  width="7" height="36" rx="2" fill="#FF4100"/>
              <rect x="11" y="7"  width="7" height="29" rx="2" fill="#FF4100" fillOpacity="0.72"/>
              <rect x="22" y="14" width="7" height="22" rx="2" fill="#FF4100" fillOpacity="0.44"/>
            </g>
          </svg>
          <div>
            <div style={{ color:'#ffffff', fontSize:'1.15rem', fontWeight:800, letterSpacing:'-0.04em', lineHeight:1 }}>
              WIBusiness
            </div>
            <div style={{ color:'rgba(255,255,255,0.30)', fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.07em', textTransform:'uppercase', marginTop:3 }}>
              by Wikeys
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <div style={{
        background:'#0D0F14',
        border:'1px solid rgba(255,255,255,0.09)',
        borderRadius:22,
        padding:'36px 32px',
        width:'100%', maxWidth:440,
        boxShadow:'0 32px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.07)',
        backdropFilter:'blur(40px)',
        WebkitBackdropFilter:'blur(40px)',
        position:'relative', zIndex:1,
      }}>
        {children}
      </div>

      <style>{`@keyframes sp-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Styles partagés ── */
const s = {
  iconWrap: (bg, border) => ({
    width:56, height:56, borderRadius:16,
    background:bg, border:`1.5px solid ${border}`,
    display:'flex', alignItems:'center', justifyContent:'center',
    margin:'0 auto 20px',
  }),
  title: {
    margin:'0 0 8px',
    fontSize:'1.4rem',
    fontWeight:800,
    color:'#ffffff',
    letterSpacing:'-0.03em',
    lineHeight:1.2,
  },
  sub: {
    margin:0,
    fontSize:'0.875rem',
    color:'rgba(255,255,255,0.45)',
    lineHeight:1.65,
  },
  label: {
    display:'block',
    fontSize:'0.78rem',
    fontWeight:700,
    color:'rgba(255,255,255,0.60)',
    marginBottom:7,
    textTransform:'uppercase',
    letterSpacing:'0.06em',
  },
  input: {
    width:'100%',
    padding:'12px 44px 12px 14px',
    border:'1.5px solid rgba(255,255,255,0.10)',
    borderRadius:11,
    fontSize:'0.9375rem',
    outline:'none',
    boxSizing:'border-box',
    fontFamily:'inherit',
    transition:'border-color 0.15s, box-shadow 0.15s, background 0.15s',
    color:'#ffffff',
    background:'#16181F',
  },
  eye: {
    position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
    background:'none', border:'none', cursor:'pointer',
    color:'rgba(255,255,255,0.35)', padding:0,
  },
  btnPrimary: {
    display:'block',
    padding:'14px 24px',
    background:'#3B82F6',
    color:'white',
    border:'none',
    borderRadius:12,
    fontWeight:800,
    fontSize:'0.9375rem',
    cursor:'pointer',
    textAlign:'center',
    boxShadow:'0 12px 28px rgba(59,130,246,0.38)',
    transition:'opacity 0.15s, background 0.15s, box-shadow 0.15s',
    letterSpacing:'-0.01em',
  },
};
